/**
 * Beacon API Service Layer.
 *
 * Single source of truth for backend calls; each method is a thin
 * wrapper over fetchAsync that formats the URL and request body.
 */
import { fetchAsync } from '../utils/httpUtil';
import { GetPaymentDetailInput, GetPaymentDetailOutput } from '../store/slices/paymentDetail/paymentDetailApi';
import { GetArtifactDetailInput, GetArtifactDetailOutput } from '../store/slices/artifactDetail/artifactDetailApi';
import { SearchPaymentsInput, SearchPaymentsOutput } from '../store/slices/searchPayments/searchPaymentsApi';
import { SearchArtifactsInput, SearchArtifactsOutput } from '../store/slices/searchArtifacts/searchArtifactsApi';
import { SearchExecutionsInput, SearchExecutionsOutput } from '../store/slices/searchExecutions/searchExecutionsApi';
import { GetExecutionDetailInput, GetExecutionDetailOutput } from '../store/slices/executionDetail/executionDetailApi';

export const beaconApi = {
  /**
   * POST /payments/detail — full per-leg flow (outbound systems, inbound
   * responses) for 1–10 identifiers, with artifact and execution refs.
   */
  getPaymentDetail: (endToEndIds: string[], apiBase: string): Promise<GetPaymentDetailOutput> => {
    const input: GetPaymentDetailInput = { endToEndIds };
    return fetchAsync(`${apiBase}/payments/detail`, 'POST', JSON.stringify(input));
  },

  /**
   * POST /artifacts/detail — full per-artifact flow (directions, systems,
   * statuses) with execution refs for 1–100 artifactIds.
   */
  getArtifactDetail: (artifactIds: string[], apiBase: string): Promise<GetArtifactDetailOutput> => {
    const input: GetArtifactDetailInput = { artifactIds };
    return fetchAsync(`${apiBase}/artifacts/detail`, 'POST', JSON.stringify(input));
  },

  /**
   * POST /executions/detail — full per-execution flow (directions, systems,
   * statuses) with the execution payload for 1–100 executionIds.
   */
  getExecutionDetail: (executionIds: string[], apiBase: string): Promise<GetExecutionDetailOutput> => {
    const input: GetExecutionDetailInput = { executionIds };
    return fetchAsync(`${apiBase}/executions/detail`, 'POST', JSON.stringify(input));
  },

  /**
   * POST /payments/search — paginated search over payments by filter. Used both
   * by bulk payment search and to find the payments carried by an artifact /
   * execution (paymentIdentifiers = [id]).
   */
  searchPayments: (input: SearchPaymentsInput, apiBase: string): Promise<SearchPaymentsOutput> =>
    fetchAsync(`${apiBase}/payments/search`, 'POST', JSON.stringify(input)),

  /** POST /artifacts/search — paginated search over artifacts by filter. */
  searchArtifacts: (input: SearchArtifactsInput, apiBase: string): Promise<SearchArtifactsOutput> =>
    fetchAsync(`${apiBase}/artifacts/search`, 'POST', JSON.stringify(input)),

  /** POST /executions/search — paginated search over executions by filter. */
  searchExecutions: (input: SearchExecutionsInput, apiBase: string): Promise<SearchExecutionsOutput> =>
    fetchAsync(`${apiBase}/executions/search`, 'POST', JSON.stringify(input)),
};
